import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setShowContextMenu,
  setRequestedContextPosition,
} from "@/store/iframeSlice";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TempoElement } from "@/utils/tempoElement";
import {
  DuplicateChange,
  RemoveJsxChange,
  WrapDivChange,
  WRAP_IN_DIV_PLACEHOLDER_CODEBASE_ID,
  DUPLICATE_PLACEHOLDER_PREFIX,
} from "@/store/zustand/changeLedgerTypes";
import {
  CursorType,
  ModalType,
  setCursorType,
  setModal,
} from "@/store/globalSettingsSlice";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import { JsxElementType } from "@/utils/jsxElementTypes";
import { useAssetComponents } from "@/hooks/useAssetComponents";
import { getLookupKeyForComponent } from "@/utils/extractElementsUtils";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  useActiveCanvas,
  useCanvasId,
} from "@/utils/utils";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { useDeleteStoryboards } from "../../hooks/storyboards/useDeleteStoryboards";
import { useDuplicateStoryboard } from "../../hooks/storyboards/useDuplicateStoryboard";
import { toast } from "sonner";
import { useSaveStoryboard } from "@/hooks/storyboards/useSaveStoryboard";
import { ACTION_TYPES } from "@/utils/ai/utils";
import {
  AI_AGENT,
  AI_MODEL,
  useAiActionStore,
} from "@/store/zustand/aiActionStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { Blocks } from "lucide-react";
import useDesignSystemStore, {
  GenerationStatus,
} from "@/store/zustand/aiDesignSystemBuilder";
import { Storyboard } from "@/utils/types";
import { useFeatureFlagEnabled } from "posthog-js/react";
import useLocalMode from "@/hooks/useLocalMode";
import { useFilePathToFileTreeLookup } from "./hooks/useFileTreeLookup";
import axios from "axios";

import { useSession } from "@clerk/clerk-react";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { useSendMessage } from "@/components/AI/chat/useSendMessage";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
interface Props {
  showMenu: boolean;
  left: number;
  top: number;
  tempoElement: TempoElement;
  multiselectedElements: TempoElement[];
}

export enum DualContextMenuOptions {
  // Both storyboard & element controls
  Duplicate = "Duplicate",
  Delete = "Delete",
  OpenInVSCode = "OpenInVSCode",
  OpenInEditor = "OpenInEditor",
  AIEdit = "AIEdit",
  DesignSystemContextNew = "DesignSystemContextNew",
  DesignSystemContextExisting = "DesignSystemContextExisting",
}

export enum ElementContextMenuOptions {
  // Element only controls
  WrapInDiv = "WrapInDiv",
  EditComponent = "EditComponent",
  CreateStoryForComponent = "CreateStoryForComponent",
}

export enum StoryboardContextMenuOptions {
  // Storyboard only controls
  Publish = "Publish",
  SaveAsComponent = "SaveAsComponent",
  SaveAsStory = "SaveAsStory",
}

const CanvasContextMenu: React.FC<Props> = React.memo(
  ({ left, top, showMenu, tempoElement, multiselectedElements }) => {
    const canvasId = useCanvasId();

    const { isLocalMode, localFilePath } = useLocalMode();

    const [publishLoading, setPublishLoading] = useState(false);

    const { session } = useSession();
    const publishComponentEnabled = useFeatureFlagEnabled("publish-component");
    const setActionType = useAiActionStore((store) => store.setActionType);

    const executeCommand = useDesignCommandManager(
      (store) => store.executeCommand
    );

    const { sendMessage } = useSendMessage();
    const aiChatMessages = useSupabaseStore((state) => state.aiChatMessages);
    const selectedConversationId = useAiActionStore(
      (state) => state.selectedConversationId
    );

    // Extract the selected storyboards if the selected elements are storyboards
    const activeCanvas = useActiveCanvas();
    const { selectedStoryboard, multiselectedStoryboards } = useMemo(() => {
      if (!activeCanvas?.storyboards || !tempoElement.isStoryboard()) {
        return {};
      }

      let selectedStoryboard: Storyboard | null = null;
      let multiselectedStoryboards = [];

      activeCanvas.storyboards.forEach((sb) => {
        if (tempoElement.isStoryboard(sb.id)) {
          selectedStoryboard = sb;
        }

        if (
          multiselectedElements?.find((element) => element.isStoryboard(sb.id))
        ) {
          // @ts-expect-error TS2345
          multiselectedStoryboards.push(sb);
        }
      });

      return {
        selectedStoryboard,
        multiselectedStoryboards,
      };
    }, [activeCanvas, tempoElement, multiselectedElements]);

    const { deleteStoryboards, renderConfirmDeleteDialog } =
      useDeleteStoryboards();
    const duplicateStoryboard = useDuplicateStoryboard();
    const { saveStoryboard, renderConfirmSaveAsModal } = useSaveStoryboard();

    const dispatch = useDispatch();

    const treeElementLookup = useCodebaseStore(
      (state) => state.treeElementLookup
    );

    const setNewLayout = useLayoutStore((state) => state.setNewLayout);

    const setCanvasComponentKey = useCanvasStore(
      (store) => store.setCanvasComponentKey
    );

    const addFile = useDesignSystemStore((state) => state.addFile);
    const addExample = useDesignSystemStore((state) => state.addExample);

    const fileToContentsFromApi = useCodebaseStore(
      (state) => state.fileToContents
    );
    const filePathToFileTreeLookup = useFilePathToFileTreeLookup();

    const status = useDesignSystemStore((state) => state.generation.status);
    const resetGeneration = useDesignSystemStore(
      (state) => state.resetGeneration
    );

    const basePathToProject = useSelector(
      (state: any) => state.codebase.basePathToProject
    );

    const selectedTreeElement = (treeElementLookup || {})[
      tempoElement.codebaseId
    ];

    // For component instances only, fetch the source component from the tree elements
    const { componentKeyToComponent } = useAssetComponents();
    const sourceComponent = useMemo(() => {
      if (!componentKeyToComponent) {
        return;
      }

      if (selectedTreeElement?.type !== JsxElementType.ComponentInstance) {
        return null;
      }

      const key = getLookupKeyForComponent(selectedTreeElement);
      // @ts-expect-error TS2538
      return componentKeyToComponent[key];
    }, [selectedTreeElement, componentKeyToComponent]);

    // Helper function to resolve relative paths
    const resolveRelativePath = (
      baseDir: string,
      relativePath: string
    ): string => {
      const baseParts = baseDir.split("/").filter((part) => part !== "");
      const relativeParts = relativePath
        .split("/")
        .filter((part) => part !== "");

      const result = [...baseParts];

      for (const part of relativeParts) {
        if (part === "..") {
          result.pop();
        } else if (part !== ".") {
          result.push(part);
        }
      }

      return "/" + result.join("/");
    };

    // Utility function to gather component files for publishing
    const gatherComponentFiles = useMemo(() => {
      return async (
        storyboard: Storyboard,
        treeElement: any
      ): Promise<{ success: true; files: any[] } | { error: string }> => {
        const componentFiles: any[] = [];

        if (!storyboard?.fullFilePath || !fileToContentsFromApi) {
          return { error: "Missing storyboard file path or file cache" };
        }

        // Helper function to load file contents if not in cache
        const loadFileContents = async (
          filePath: string
        ): Promise<string | null> => {
          // First check if it's already in cache
          if (fileToContentsFromApi[filePath]) {
            return fileToContentsFromApi[filePath];
          }

          // Try to load from API
          try {
            const response = await axios.get(
              `${
                import.meta.env.VITE_BACKEND_BASE_URL
              }/canvases/${canvasId}/file`,
              {
                params: { filename: filePath },
                headers: {
                  Authorization: `Bearer ${await getUnexpiredToken(session)}`,
                },
              }
            );

            if (
              response.data?.fileContents &&
              response.data.fileContents !== "FILE_DOES_NOT_EXIST"
            ) {
              return response.data.fileContents;
            }
          } catch (error) {
            // Show warning but don't block publish
            toast.warning(
              `Could not include dependency file: ${filePath.split("/").pop()}`
            );
          }

          return null;
        };

        // Add the main storyboard file
        const mainFileContents = fileToContentsFromApi[storyboard.fullFilePath];
        if (mainFileContents) {
          const fileName = storyboard.fullFilePath.split("/").pop() || "";
          componentFiles.push({
            content: mainFileContents,
            filename: storyboard.fullFilePath,
            isPrimary: true,
            relativePath: fileName,
          });

          // Analyze imports and include related component files
          const importedFiles = new Set<string>();
          const importRegex = /import\s+.*?\s+from\s+['"]([^'"]+)['"]/g;
          let match;

          while ((match = importRegex.exec(mainFileContents)) !== null) {
            const importPath = match[1];

            // Only process relative imports (starts with . or ./)
            if (importPath.startsWith("./") || importPath.startsWith("../")) {
              // Resolve the relative path to absolute path
              const baseDir = storyboard.fullFilePath
                .split("/")
                .slice(0, -1)
                .join("/");
              const resolvedPath = resolveRelativePath(baseDir, importPath);

              // Try common extensions if no extension provided
              const possiblePaths = [
                resolvedPath,
                resolvedPath + ".tsx",
                resolvedPath + ".ts",
                resolvedPath + "/index.tsx",
                resolvedPath + "/index.ts",
              ];

              for (const possiblePath of possiblePaths) {
                if (!importedFiles.has(possiblePath)) {
                  const fileContents = await loadFileContents(possiblePath);
                  if (fileContents) {
                    importedFiles.add(possiblePath);
                    const importedFileName =
                      possiblePath.split("/").pop() || "";
                    componentFiles.push({
                      content: fileContents,
                      filename: possiblePath,
                      isPrimary: false,
                      relativePath: importedFileName,
                    });
                    break;
                  }
                }
              }
            }
          }
        }

        // Critical check: Ensure main component was included
        if (
          componentFiles.length === 0 ||
          !componentFiles.find((f) => f.isPrimary)
        ) {
          return {
            error:
              "Main component file could not be loaded. Cannot publish without the primary component.",
          };
        }

        return { success: true, files: componentFiles };
      };
    }, [fileToContentsFromApi, canvasId, session]);

    // Utility function to gather external dependencies for publishing
    const gatherExternalDependencies = useMemo(() => {
      return (componentFiles: any[]) => {
        const externalDependencies: string[] = [];

        // Basic pattern matching for common dependencies
        componentFiles.forEach((file) => {
          const fileContents = file.content || file.file_contents;
          if (!fileContents) return;

          // Extract import statements
          const importRegex = /import\s+.*?\s+from\s+['"]([^'"]+)['"]/g;
          let match;

          while ((match = importRegex.exec(fileContents)) !== null) {
            const importPath = match[1];

            // Only include external packages (not relative imports)
            if (!importPath.startsWith(".") && !importPath.startsWith("/")) {
              // Extract package name (handle scoped packages)
              const packageName = importPath.startsWith("@")
                ? importPath.split("/").slice(0, 2).join("/")
                : importPath.split("/")[0];

              // Avoid duplicates
              if (!externalDependencies.includes(packageName)) {
                externalDependencies.push(packageName);
              }
            }
          }
        });

        return externalDependencies;
      };
    }, []);

    const onClickElemContextMenu = async (
      contextMenuOption:
        | DualContextMenuOptions
        | ElementContextMenuOptions
        | StoryboardContextMenuOptions
    ) => {
      if (tempoElement.isEmpty()) {
        return;
      }

      switch (contextMenuOption) {
        case DualContextMenuOptions.Delete:
          if (selectedStoryboard) {
            deleteStoryboards(
              multiselectedElements?.length
                ? multiselectedElements
                : [tempoElement]
            );
            break;
          }

          const removeChange = new RemoveJsxChange({
            codebaseIdsToRemove: multiselectedElements?.length
              ? multiselectedElements.map(
                  (tempoElement) => tempoElement.codebaseId
                )
              : [tempoElement.codebaseId],
          });

          executeCommand(removeChange);

          // After instant update, deselect
          removeChange.clearSelectedElementsAfterInstantUpdate();

          // After undo, reselect
          removeChange.setSelectedElementsAfterUndoInstantUpdate(
            tempoElement.getKey(),
            multiselectedElements?.map((e) => e.getKey())
          );

          break;
        case DualContextMenuOptions.Duplicate:
          if (selectedStoryboard) {
            // @ts-expect-error TS2339
            if (!multiselectedStoryboards?.length) {
              duplicateStoryboard(selectedStoryboard);
            }

            break;
          }

          const dupChange = new DuplicateChange({
            codebaseIdsToDuplicate: multiselectedElements?.length
              ? multiselectedElements.map(
                  (tempoElement) => tempoElement.codebaseId
                )
              : [tempoElement.codebaseId],
          });

          const getNewTempoElement = (
            te: TempoElement,
            numDuplicatedBefore: number
          ): TempoElement => {
            const uniquePathSegments = te.uniquePath.split("-");
            const newUniquePath = `${uniquePathSegments
              .slice(0, -1)
              .join("-")}-${
              Number(uniquePathSegments.slice(-1)) + 1 + numDuplicatedBefore
            }`;

            return new TempoElement(
              DUPLICATE_PLACEHOLDER_PREFIX + te.codebaseId,
              tempoElement.storyboardId,
              newUniquePath
            );
          };

          let newSelectedElementKey;

          if (!multiselectedElements?.length) {
            // Select the new element that was duplicated
            const newSelectedElement = getNewTempoElement(tempoElement, 0);
            newSelectedElementKey = newSelectedElement.getKey();
          }

          const newMultiselectedElementKeys: string[] = multiselectedElements
            ?.sort((a, b) => a.uniquePath.localeCompare(b.uniquePath))
            ?.map((te, i) => {
              const newElement = getNewTempoElement(te, i);

              if (te.isEqual(tempoElement)) {
                newSelectedElementKey = newElement.getKey();
              }

              return newElement.getKey();
            });

          dupChange.setSelectedElementsAfterInstantUpdate(
            newSelectedElementKey,
            newMultiselectedElementKeys
          );

          executeCommand(dupChange);
          break;
        case DualContextMenuOptions.OpenInEditor:
          dispatch(
            setCursorType({
              cursorType: CursorType.Pointer,
            })
          );
          // @ts-expect-error TS2345
          setNewLayout(LeftTab.FileSelector, null);
          break;
        case DualContextMenuOptions.OpenInVSCode:
          if (!localFilePath) {
            toast.error(
              'Local file path not found, please try re-running "npx run-tempo"'
            );
            break;
          }

          let localPath;

          let firstPathSegment = localFilePath;
          let secondPathSegment = selectedStoryboard
            ? // @ts-expect-error TS2339
              selectedStoryboard?.fullFilePath?.split?.(canvasId)[1]
            : selectedTreeElement?.filename?.split?.(canvasId)?.[1];

          if (firstPathSegment && secondPathSegment) {
            if (firstPathSegment.endsWith("/")) {
              firstPathSegment = firstPathSegment.slice(0, -1);
            }

            if (secondPathSegment.startsWith("/")) {
              secondPathSegment = secondPathSegment.slice(1);
            }

            localPath = `${firstPathSegment}/${secondPathSegment}`;
          }

          const startLine = selectedStoryboard
            ? 0
            : selectedTreeElement?.startLine;

          if (localPath) {
            // Construct the URI
            const absolutePath = encodeURI(localPath);

            let vscodeUri = `vscode://file${absolutePath}`;

            if (startLine !== null && startLine !== undefined) {
              const encodedLineNumber = encodeURIComponent(startLine);
              vscodeUri += `:${encodedLineNumber}`;
            }

            window.open(vscodeUri, "_blank");
          }
          break;
        case DualContextMenuOptions.AIEdit:
          dispatch(
            setCursorType({
              cursorType: CursorType.Pointer,
            })
          );
          setActionType(ACTION_TYPES.EDIT);
          dispatch(setModal(ModalType.AI));
          break;
        case DualContextMenuOptions.DesignSystemContextNew:
        case DualContextMenuOptions.DesignSystemContextExisting:
          // TOOD: Add file to selectedFiles using FileData
          // Add example by substringing code

          if (
            contextMenuOption === DualContextMenuOptions.DesignSystemContextNew
          ) {
            resetGeneration();
          }

          const file = selectedTreeElement.filename;

          if (selectedTreeElement.type === JsxElementType.ComponentInstance) {
            const componentInstanceFile =
              (basePathToProject || "") +
              // @ts-expect-error TS18048
              selectedTreeElement.componentSourceImport.filename;

            // componentInstanceFile represents a key in filePathToFileTreeLookup without the extension

            const fileInLookup = Object.keys(filePathToFileTreeLookup).find(
              (key) => key.startsWith(componentInstanceFile)
            );

            if (
              componentInstanceFile &&
              // @ts-expect-error TS2538
              filePathToFileTreeLookup[fileInLookup]
            ) {
              const componentInstanceFileData =
                // @ts-expect-error TS2538
                filePathToFileTreeLookup[fileInLookup];
              addFile(componentInstanceFileData);
            }
          } else {
            if (filePathToFileTreeLookup[file]) {
              addFile(filePathToFileTreeLookup[file]);
            }

            if (fileToContentsFromApi[file]) {
              const contents = fileToContentsFromApi[file];
              const exampleSubstring = contents.substring(
                // @ts-expect-error TS2345
                selectedTreeElement.contentsStart,
                selectedTreeElement.contentsEnd
              );

              if (exampleSubstring.length) {
                addExample(exampleSubstring);
              }
            }
          }
          break;
        case ElementContextMenuOptions.EditComponent:
          if (sourceComponent) {
            // @ts-expect-error TS2345
            setCanvasComponentKey(getLookupKeyForComponent(sourceComponent));
          }
          break;
        case ElementContextMenuOptions.CreateStoryForComponent:
          if (sourceComponent?.componentName) {
            // Get the current conversation messages
            const conversationMessages = Object.values(aiChatMessages)
              .filter((msg) => msg.conversation_id === selectedConversationId)
              .sort(
                (a, b) =>
                  new Date(a.created_at).getTime() -
                  new Date(b.created_at).getTime()
              );

            sendMessage({
              userPrompt: `Create or update story for Component - ${sourceComponent.componentName}`,
              sendChatMessageFiles: [],
              refreshAfterResponse: true,
              chatOnlyMode: false,
              isFreePrompt: false,
              useReasoning: false,
              useSearch: false,
              reasoningModel: useAiActionStore.getState().reasoningModel,
              codeGenerationModel: AI_MODEL.CLAUDE_4_0_SONNET,
              messagesCanSummarize: conversationMessages.slice(0, -1),
              messagesDoNotSummarize: conversationMessages.slice(-1),
              agentChoice: AI_AGENT.MAX_AGENT,
            });
          }
          break;
        case ElementContextMenuOptions.WrapInDiv:
          let codebaseIdsToWrap = multiselectedElements?.length
            ? multiselectedElements
                .sort((a, b) => a.uniquePath.localeCompare(b.uniquePath))
                .map((tempoElement) => tempoElement.codebaseId)
            : [tempoElement.codebaseId];

          const change = new WrapDivChange({ codebaseIdsToWrap });

          // Select the new div that was wrapped
          const newSelectedElement = new TempoElement(
            WRAP_IN_DIV_PLACEHOLDER_CODEBASE_ID,
            tempoElement.storyboardId,
            tempoElement.uniquePath
          );
          change.setSelectedElementsAfterInstantUpdate(
            newSelectedElement.getKey(),
            null
          );
          executeCommand(change);
          break;
        case StoryboardContextMenuOptions.Publish:
          if (publishLoading) {
            return;
          }

          // @ts-expect-error TS2339
          if (!selectedStoryboard || multiselectedStoryboards?.length) {
            break;
          }

          if (
            // @ts-expect-error TS2339
            !selectedStoryboard.saved ||
            // @ts-expect-error TS2339
            !selectedStoryboard.savedCodebaseId
          ) {
            toast.warning("Component not saved, couldn't publish");
            break;
          }

          const treeElement =
            // @ts-expect-error TS2339
            treeElementLookup[selectedStoryboard.savedCodebaseId];

          if (!treeElement) {
            toast.error(
              "Component data not found. Please save the component again before publishing."
            );
            break;
          }

          // Gather component files and external dependencies for publishing
          const componentResult = await gatherComponentFiles(
            selectedStoryboard,
            treeElement
          );

          if ("error" in componentResult) {
            toast.error(componentResult.error);
            break;
          }

          const componentFiles = componentResult.files;
          const externalDependencies =
            gatherExternalDependencies(componentFiles);

          const supabaseJWT = await getUnexpiredToken(session);

          try {
            setPublishLoading(true);
            toast.loading("Publishing component...");

            const response = await axios.post(
              `${
                import.meta.env.VITE_BACKEND_BASE_URL
              }/canvases/${canvasId}/publishComponent`,
              {
                main_file: JSON.stringify(selectedStoryboard),
                component_files: componentFiles,
                extra_deps: externalDependencies,
                treeElement: treeElement,
              },
              {
                headers: {
                  Authorization: `Bearer ${supabaseJWT}`,
                },
              }
            );

            const componentId = response.data?.componentId;
            if (componentId) {
              // Redirect to React Components website with the new component ID
              window.open(
                `${
                  import.meta.env.VITE_REACT_COMPONENTS_URL
                }/?new-component-id=${componentId}`,
                "_blank"
              );
              toast.success("Published component successfully");
            } else {
              toast.error(
                "Publishing failed: No component ID returned from server"
              );
            }
          } catch (error) {
            const message = getAxiosErorrMessage(error);
            toast.error(`Could not publish: ${message}`);
          } finally {
            setPublishLoading(false);
          }
          break;
        case StoryboardContextMenuOptions.SaveAsComponent:
          // @ts-expect-error TS2339
          if (!selectedStoryboard || multiselectedStoryboards?.length) {
            break;
          }

          saveStoryboard(selectedStoryboard, "component");
          break;
        case StoryboardContextMenuOptions.SaveAsStory:
          if (!selectedStoryboard) {
            break;
          }

          saveStoryboard(selectedStoryboard, "story");
          break;
        default:
          // Unknown context menu option
          break;
      }

      dismissRequestedContext();
    };

    const dismissRequestedContext = () => {
      dispatch(setShowContextMenu(false));
      dispatch(setRequestedContextPosition(null));
    };

    /**
     * For selected storyboards, indicates how many of each type are selected
     */
    const selectionText = useMemo(() => {
      if (!selectedStoryboard) {
        return "";
      }

      let numStoryboards = 0;
      let numStories = 0;

      // @ts-expect-error TS2339
      const storyboards = multiselectedStoryboards?.length
        ? multiselectedStoryboards
        : [selectedStoryboard];

      // Count types
      storyboards.forEach((storyboard) => {
        // @ts-expect-error TS2339
        storyboard.type === STORYBOARD_TYPES.STORY
          ? numStories++
          : numStoryboards++;
      });

      let text = " ";
      if (numStoryboards) {
        if (!numStories && numStoryboards === 1) {
          text += "Storyboard";
        } else {
          text += `${numStoryboards} Storyboard${
            numStoryboards > 1 ? "s" : ""
          }`;
        }
      }

      if (numStories) {
        if (!numStoryboards && numStories === 1) {
          text += "Story";
        } else {
          if (text) {
            text += ", ";
          }

          text += `${numStories} Stor${numStories > 1 ? "ies" : "y"}`;
        }
      }

      return text;
    }, [multiselectedStoryboards, selectedStoryboard]);

    // TODO: Scrolling when context menu doesn't dismiss as it did before
    return (
      <>
        {showMenu && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 9999, // Make sure this is less than your menu's zIndex
            }}
            className="pointer-events-auto"
            onClick={dismissRequestedContext}
            onContextMenu={(e) => {
              e.preventDefault();
              dismissRequestedContext();
            }}
            onWheel={dismissRequestedContext}
            onScroll={dismissRequestedContext}
            onTouchStart={dismissRequestedContext}
          />
        )}
        <DropdownMenu open={showMenu}>
          <DropdownMenuTrigger
            style={{ position: "fixed", top: top, left: left }}
          />
          <DropdownMenuContent
            side="right"
            style={{ zIndex: 10000 }}
            className="w-56"
            onInteractOutside={dismissRequestedContext}
            onFocusOutside={dismissRequestedContext}
          >
            <DropdownMenuLabel>
              <span>
                {selectedStoryboard ? "Modify Storyboard" : "Modify Element"}
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {selectedStoryboard &&
                // @ts-expect-error TS2339
                !selectedStoryboard.savedCodebaseId &&
                // @ts-expect-error TS2339
                !multiselectedStoryboards?.length &&
                // @ts-expect-error TS2339
                selectedStoryboard.type === STORYBOARD_TYPES.COMPONENT && (
                  <DropdownMenuItem
                    onClick={() =>
                      onClickElemContextMenu(
                        StoryboardContextMenuOptions.SaveAsComponent
                      )
                    }
                  >
                    <p>Save As Component</p>
                  </DropdownMenuItem>
                )}
              {selectedStoryboard &&
                // @ts-expect-error TS2339
                !selectedStoryboard.savedCodebaseId &&
                // @ts-expect-error TS2339
                !multiselectedStoryboards?.length &&
                // @ts-expect-error TS2339
                selectedStoryboard.type === STORYBOARD_TYPES.COMPONENT && (
                  <DropdownMenuItem
                    onClick={() =>
                      onClickElemContextMenu(
                        StoryboardContextMenuOptions.SaveAsStory
                      )
                    }
                  >
                    <p>Save As Story</p>
                  </DropdownMenuItem>
                )}
              {/*
               // @ts-expect-error TS2339 */}
              {selectedStoryboard?.savedCodebaseId &&
                // @ts-expect-error TS2339
                !multiselectedStoryboards?.length &&
                // @ts-expect-error TS2339
                selectedStoryboard.type === STORYBOARD_TYPES.COMPONENT &&
                !isLocalMode &&
                publishComponentEnabled && (
                  <DropdownMenuItem
                    onClick={() =>
                      onClickElemContextMenu(
                        StoryboardContextMenuOptions.Publish
                      )
                    }
                  >
                    <p>Publish to Marketplace</p>
                  </DropdownMenuItem>
                )}
              {!selectedStoryboard && (
                <DropdownMenuItem
                  onClick={() =>
                    onClickElemContextMenu(ElementContextMenuOptions.WrapInDiv)
                  }
                >
                  <p>
                    Wrap in <code className="text-xs">{"<div>"}</code>
                  </p>
                </DropdownMenuItem>
              )}
              {/* Only allow duplicating for a single non-story storyboard, or for elements */}
              {(!selectedStoryboard ||
                // @ts-expect-error TS2339
                (selectedStoryboard.type !== STORYBOARD_TYPES.STORY &&
                  // @ts-expect-error TS2339
                  !multiselectedStoryboards?.length)) && (
                <DropdownMenuItem
                  onClick={() =>
                    onClickElemContextMenu(DualContextMenuOptions.Duplicate)
                  }
                >
                  Duplicate
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() =>
                  onClickElemContextMenu(DualContextMenuOptions.Delete)
                }
              >
                Delete{selectionText}
                <DropdownMenuShortcut>⌫</DropdownMenuShortcut>
              </DropdownMenuItem>
              {isLocalMode && !multiselectedElements?.length && (
                <DropdownMenuItem
                  onClick={() =>
                    onClickElemContextMenu(DualContextMenuOptions.OpenInVSCode)
                  }
                >
                  Open in VSCode
                </DropdownMenuItem>
              )}
              {!isLocalMode && !multiselectedElements?.length && (
                <DropdownMenuItem
                  onClick={() =>
                    onClickElemContextMenu(DualContextMenuOptions.OpenInEditor)
                  }
                >
                  View Code
                </DropdownMenuItem>
              )}
              {(selectedStoryboard || tempoElement.isKnownElement()) && (
                <DropdownMenuItem
                  onClick={() =>
                    onClickElemContextMenu(DualContextMenuOptions.AIEdit)
                  }
                >
                  Edit With AI
                </DropdownMenuItem>
              )}
              {!selectedStoryboard && sourceComponent && (
                <DropdownMenuItem
                  onClick={() =>
                    onClickElemContextMenu(
                      ElementContextMenuOptions.EditComponent
                    )
                  }
                >
                  Edit Component
                </DropdownMenuItem>
              )}
              {!selectedStoryboard && sourceComponent && (
                <DropdownMenuItem
                  onClick={() => {
                    onClickElemContextMenu(
                      ElementContextMenuOptions.CreateStoryForComponent
                    );
                  }}
                >
                  Create Story for Component
                </DropdownMenuItem>
              )}
              {!selectedStoryboard &&
                tempoElement.isKnownElement() &&
                !multiselectedElements?.length && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <Blocks className="w-5 h-5 mr-2" />
                      <span>Design System</span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent>
                        {status > GenerationStatus.Pending && (
                          <DropdownMenuItem
                            key={"ds-add-existing"}
                            onSelect={() => {
                              onClickElemContextMenu(
                                DualContextMenuOptions.DesignSystemContextExisting
                              );
                            }}
                          >
                            Add context to workflow
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          key={"ds-add-element-to-new"}
                          onSelect={() => {
                            onClickElemContextMenu(
                              DualContextMenuOptions.DesignSystemContextNew
                            );
                          }}
                        >
                          Build new component with context
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                )}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        {renderConfirmDeleteDialog()}
        {renderConfirmSaveAsModal()}
      </>
    );
  }
);

export default CanvasContextMenu;
